import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { PlaceCategory } from '../api/placeCategory'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminInputTooltip from '../components/AdminInputTooltip'
import AdminInput from '../components/AdminInput'

const AdminPlaceCategories: React.FC = () => {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<PlaceCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] =
    useState<PlaceCategory | null>(null)
  const [tooltipField, setTooltipField] = useState<
    'icon' | 'marker_pin' | 'marker_pin_bookmarked' | 'category_icon_brown' | null
  >(null)
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    icon: null as File | null,
    marker_pin: null as File | null,
    marker_pin_bookmarked: null as File | null,
    category_icon_brown: null as File | null,
  })
  const [iconPreviews, setIconPreviews] = useState<{
    icon: string | null
    marker_pin: string | null
    marker_pin_bookmarked: string | null
    category_icon_brown: string | null
  }>({
    icon: null,
    marker_pin: null,
    marker_pin_bookmarked: null,
    category_icon_brown: null,
  })

  const fetchCategories = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getPlaceCategories()
      setCategories(response.data)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('장소 카테고리 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchCategories()
  }, [navigate, fetchCategories])

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const handleCreate = () => {
    setFormData({
      code: '',
      name: '',
      icon: null,
      marker_pin: null,
      marker_pin_bookmarked: null,
      category_icon_brown: null,
    })
    setIconPreviews({
      icon: null,
      marker_pin: null,
      marker_pin_bookmarked: null,
      category_icon_brown: null,
    })
    setIsCreateModalOpen(true)
  }

  const handleEdit = (category: PlaceCategory) => {
    setSelectedCategory(category)
    setFormData({
      code: category.code,
      name: category.name,
      icon: null,
      marker_pin: null,
      marker_pin_bookmarked: null,
      category_icon_brown: null,
    })
    setIconPreviews({
      icon: category.icon_path,
      marker_pin: category.marker_pin_path,
      marker_pin_bookmarked: category.marker_pin_bookmarked_path,
      category_icon_brown: category.category_icon_brown_path,
    })
    setIsEditModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    const category = categories.find((c) => c.id === id)
    if (!category) return

    if (
      !window.confirm(
        `정말 "${category.name}" 카테고리를 삭제하시겠습니까?\n\n이 카테고리를 사용하는 장소가 ${category.place_count}개 있습니다.`
      )
    ) {
      return
    }

    try {
      await adminApi.deletePlaceCategory(id)
      toastUtil.success('장소 카테고리가 삭제되었습니다')
      fetchCategories()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          '장소 카테고리 삭제 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const handleIconChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field:
      | 'icon'
      | 'marker_pin'
      | 'marker_pin_bookmarked'
      | 'category_icon_brown'
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      setFormData({ ...formData, [field]: file })
      // 미리보기 생성
      const reader = new FileReader()
      reader.onloadend = () => {
        setIconPreviews({ ...iconPreviews, [field]: reader.result as string })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmitCreate = async () => {
    if (!formData.code.trim()) {
      toastUtil.error('카테고리 코드를 입력해주세요')
      return
    }
    if (!formData.name.trim()) {
      toastUtil.error('카테고리 이름을 입력해주세요')
      return
    }

    try {
      await adminApi.createPlaceCategory({
        code: formData.code.trim(),
        name: formData.name.trim(),
        icon: formData.icon,
        marker_pin: formData.marker_pin,
        marker_pin_bookmarked: formData.marker_pin_bookmarked,
        category_icon_brown: formData.category_icon_brown,
      })
      toastUtil.success('장소 카테고리가 생성되었습니다')
      setIsCreateModalOpen(false)
      setFormData({
        code: '',
        name: '',
        icon: null,
        marker_pin: null,
        marker_pin_bookmarked: null,
        category_icon_brown: null,
      })
      setIconPreviews({
        icon: null,
        marker_pin: null,
        marker_pin_bookmarked: null,
        category_icon_brown: null,
      })
      fetchCategories()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.code?.[0] ||
          error.response?.data?.errors?.name?.[0] ||
          '장소 카테고리 생성 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const handleSubmitEdit = async () => {
    if (!formData.code.trim()) {
      toastUtil.error('카테고리 코드를 입력해주세요')
      return
    }
    if (!formData.name.trim()) {
      toastUtil.error('카테고리 이름을 입력해주세요')
      return
    }

    if (!selectedCategory) return

    try {
      await adminApi.updatePlaceCategory(selectedCategory.id, {
        code: formData.code.trim(),
        name: formData.name.trim(),
        icon: formData.icon,
        marker_pin: formData.marker_pin,
        marker_pin_bookmarked: formData.marker_pin_bookmarked,
        category_icon_brown: formData.category_icon_brown,
      })
      toastUtil.success('장소 카테고리가 수정되었습니다')
      setIsEditModalOpen(false)
      setSelectedCategory(null)
      setFormData({
        code: '',
        name: '',
        icon: null,
        marker_pin: null,
        marker_pin_bookmarked: null,
        category_icon_brown: null,
      })
      setIconPreviews({
        icon: null,
        marker_pin: null,
        marker_pin_bookmarked: null,
        category_icon_brown: null,
      })
      fetchCategories()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.errors?.code?.[0] ||
          error.response?.data?.errors?.name?.[0] ||
          '장소 카테고리 수정 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                장소 카테고리 관리
              </h2>
              <button
                onClick={handleCreate}
                className={`
                  py-12px
                  px-24px
                  bg-4B3621-80%
                  text-white
                  rounded-8px
                  text-style-button2
                  hover:bg-4B3621-80%-70%
                  transition-colors
                `}
              >
                카테고리 추가
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : categories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  카테고리가 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          코드
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          이름
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          아이콘
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          사용 중인 장소 수
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          등록일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((category) => (
                        <tr
                          key={category.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {category.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {category.code}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {category.name}
                          </td>
                          <td className="py-12px px-16px">
                            {category.icon_path ? (
                              <img
                                src={category.icon_path}
                                alt={category.name}
                                className={`
                                  w-40px
                                  h-40px
                                  object-contain
                                  border
                                  border-brandPrimary-10%
                                  rounded-4px
                                  bg-backgroundColorBg2
                                `}
                              />
                            ) : (
                              <span className="text-style-body2 text-textColorText2">
                                -
                              </span>
                            )}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {category.place_count.toLocaleString()}개
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(category.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <div className="flex gap-8px">
                              <button
                                onClick={() => handleEdit(category)}
                                className={`
                                  py-8px
                                  px-16px
                                  bg-4B3621-80%
                                  text-white
                                  rounded-8px
                                  text-style-button2
                                  hover:bg-4B3621-80%-70%
                                  transition-colors
                                `}
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleDelete(category.id)}
                                disabled={category.place_count > 0}
                                className={`
                                  py-8px
                                  px-16px
                                  rounded-8px
                                  text-style-button2
                                  transition-colors
                                  ${
                                    category.place_count > 0
                                      ? 'bg-backgroundColorBg2 text-textColorText3 cursor-not-allowed'
                                      : 'bg-semanticError text-white hover:bg-semanticError-70%'
                                  }
                                `}
                              >
                                삭제
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* 생성 모달 */}
      <AdminBaseModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          setTooltipField(null)
        }}
        title="장소 카테고리 추가"
        maxWidth="max-w-[600px]"
        overlayOpacity="bg-opacity-50"
      >
        <div className="p-24px overflow-visible">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmitCreate()
            }}
            className="overflow-visible"
          >
            <div className="grid grid-cols-2 gap-24px overflow-visible">
              <AdminInput
                label="카테고리 코드"
                required
                type="text"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                placeholder="예: restaurant, cafe, bar"
              />

              <AdminInput
                label="카테고리 이름"
                required
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="예: 레스토랑, 카페, 술집"
              />

              <AdminInputTooltip
                label="카테고리 아이콘"
                tooltipText={
                  <>
                    리뷰 박스에서 가게 이름 옆에 표시되는 아이콘입니다.
                    <br />
                    마스크 이미지로 사용되어 브랜드 컬러로 표시됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'icon'}
                onTooltipMouseEnter={() => setTooltipField('icon')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.icon && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.icon}
                      alt="카테고리 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'icon')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="마커 핀 아이콘"
                tooltipText={
                  <>
                    지도 화면에서 일반 장소를 표시하는 마커 아이콘입니다.
                    <br />
                    북마크되지 않은 장소에 사용됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'marker_pin'}
                onTooltipMouseEnter={() => setTooltipField('marker_pin')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.marker_pin && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.marker_pin}
                      alt="마커 핀 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'marker_pin')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="북마크된 마커 핀 아이콘"
                tooltipText={
                  <>
                    지도 화면에서 북마크된 장소를 표시하는 마커 아이콘입니다.
                    <br />
                    사용자가 저장한 장소에 사용됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'marker_pin_bookmarked'}
                onTooltipMouseEnter={() => setTooltipField('marker_pin_bookmarked')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.marker_pin_bookmarked && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.marker_pin_bookmarked}
                      alt="북마크된 마커 핀 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'marker_pin_bookmarked')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="카테고리 아이콘 (브라운)"
                tooltipText={
                  <>
                    여러 화면에서 카테고리를 표시하는 아이콘입니다.
                    <br />
                    • 저장된 장소 카테고리 리스트
                    <br />
                    • 랭킹 페이지
                    <br />
                    • 가게 상세 페이지
                    <br />
                    • 리뷰 영수증 카드
                    <br />
                    • 가게 바텀시트
                    <br />
                    • 피드 리뷰 박스
                  </>
                }
                isTooltipOpen={tooltipField === 'category_icon_brown'}
                onTooltipMouseEnter={() => setTooltipField('category_icon_brown')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.category_icon_brown && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.category_icon_brown}
                      alt="카테고리 아이콘 브라운 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'category_icon_brown')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
                <div className="text-xs text-textColorText2 mt-4px">
                  지원 형식: JPEG, PNG, JPG, GIF, WEBP (최대 5MB)
                </div>
              </AdminInputTooltip>
            </div>

            <div className="flex justify-end gap-12px mt-32px pt-24px border-t border-brandPrimary-5%">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className={`
                  py-10px
                  px-20px
                  border
                  border-brandPrimary-20%
                  text-textColorText2
                  rounded-8px
                  text-style-button2
                  hover:bg-backgroundColorBg2
                  transition-colors
                `}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={!formData.code.trim() || !formData.name.trim()}
                className={`
                  py-10px
                  px-20px
                  bg-4B3621-80%
                  text-white
                  rounded-8px
                  text-style-button2
                  hover:bg-4B3621-80%-70%
                  transition-colors
                  disabled:bg-4B3621-80%-10%
                  disabled:text-textColorText3
                  disabled:cursor-not-allowed
                  min-w-[120px]
                `}
              >
                생성
              </button>
            </div>
          </form>
        </div>
      </AdminBaseModal>

      {/* 수정 모달 */}
      <AdminBaseModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedCategory(null)
          setTooltipField(null)
          setFormData({
            code: '',
            name: '',
            icon: null,
            marker_pin: null,
            marker_pin_bookmarked: null,
            category_icon_brown: null,
          })
          setIconPreviews({
            icon: null,
            marker_pin: null,
            marker_pin_bookmarked: null,
            category_icon_brown: null,
          })
        }}
        title="장소 카테고리 수정"
        maxWidth="max-w-[600px]"
        overlayOpacity="bg-opacity-50"
      >
        <div className="p-24px overflow-visible">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmitEdit()
            }}
            className="overflow-visible"
          >
            <div className="grid grid-cols-2 gap-24px overflow-visible">
              <AdminInput
                label="카테고리 코드"
                required
                type="text"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                placeholder="예: restaurant, cafe, bar"
              />

              <AdminInput
                label="카테고리 이름"
                required
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="예: 레스토랑, 카페, 술집"
              />

              <AdminInputTooltip
                label="카테고리 아이콘"
                tooltipText={
                  <>
                    리뷰 박스에서 가게 이름 옆에 표시되는 아이콘입니다.
                    <br />
                    마스크 이미지로 사용되어 브랜드 컬러로 표시됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'icon'}
                onTooltipMouseEnter={() => setTooltipField('icon')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.icon && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.icon}
                      alt="카테고리 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'icon')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="마커 핀 아이콘"
                tooltipText={
                  <>
                    지도 화면에서 일반 장소를 표시하는 마커 아이콘입니다.
                    <br />
                    북마크되지 않은 장소에 사용됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'marker_pin'}
                onTooltipMouseEnter={() => setTooltipField('marker_pin')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.marker_pin && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.marker_pin}
                      alt="마커 핀 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'marker_pin')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="북마크된 마커 핀 아이콘"
                tooltipText={
                  <>
                    지도 화면에서 북마크된 장소를 표시하는 마커 아이콘입니다.
                    <br />
                    사용자가 저장한 장소에 사용됩니다.
                  </>
                }
                isTooltipOpen={tooltipField === 'marker_pin_bookmarked'}
                onTooltipMouseEnter={() => setTooltipField('marker_pin_bookmarked')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.marker_pin_bookmarked && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.marker_pin_bookmarked}
                      alt="북마크된 마커 핀 아이콘 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'marker_pin_bookmarked')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
              </AdminInputTooltip>

              <AdminInputTooltip
                label="카테고리 아이콘 (브라운)"
                tooltipText={
                  <>
                    여러 화면에서 카테고리를 표시하는 아이콘입니다.
                    <br />
                    • 저장된 장소 카테고리 리스트
                    <br />
                    • 랭킹 페이지
                    <br />
                    • 가게 상세 페이지
                    <br />
                    • 리뷰 영수증 카드
                    <br />
                    • 가게 바텀시트
                    <br />
                    • 피드 리뷰 박스
                  </>
                }
                isTooltipOpen={tooltipField === 'category_icon_brown'}
                onTooltipMouseEnter={() => setTooltipField('category_icon_brown')}
                onTooltipMouseLeave={() => setTooltipField(null)}
              >
                {iconPreviews.category_icon_brown && (
                  <div className="mb-12px">
                    <img
                      src={iconPreviews.category_icon_brown}
                      alt="카테고리 아이콘 브라운 미리보기"
                      className={`
                        w-80px
                        h-80px
                        object-contain
                        border
                        border-brandPrimary-10%
                        rounded-8px
                        bg-backgroundColorBg2
                      `}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => handleIconChange(e, 'category_icon_brown')}
                  className={`
                    w-full
                    py-10px
                    px-12px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body2
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                  `}
                />
                <div className="text-xs text-textColorText2 mt-4px">
                  지원 형식: JPEG, PNG, JPG, GIF, WEBP (최대 5MB)
                </div>
              </AdminInputTooltip>
            </div>

            <div className="flex justify-end gap-12px mt-32px pt-24px border-t border-brandPrimary-5%">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false)
                  setSelectedCategory(null)
                  setFormData({
                    code: '',
                    name: '',
                    icon: null,
                    marker_pin: null,
                    marker_pin_bookmarked: null,
                    category_icon_brown: null,
                  })
                  setIconPreviews({
                    icon: null,
                    marker_pin: null,
                    marker_pin_bookmarked: null,
                    category_icon_brown: null,
                  })
                }}
                className={`
                  py-10px
                  px-20px
                  border
                  border-brandPrimary-20%
                  text-textColorText2
                  rounded-8px
                  text-style-button2
                  hover:bg-backgroundColorBg2
                  transition-colors
                `}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={!formData.code.trim() || !formData.name.trim()}
                className={`
                  py-10px
                  px-20px
                  bg-4B3621-80%
                  text-white
                  rounded-8px
                  text-style-button2
                  hover:bg-4B3621-80%-70%
                  transition-colors
                  disabled:bg-4B3621-80%-10%
                  disabled:text-textColorText3
                  disabled:cursor-not-allowed
                  min-w-[120px]
                `}
              >
                수정
              </button>
            </div>
          </form>
        </div>
      </AdminBaseModal>
    </div>
  )
}

export default AdminPlaceCategories
